import { ErrorModal, getMomentTz, useShiftNavigator } from "@dexteel/mesf-core";
import { CircularProgress, Grid, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { useEffect, useState } from "react";
import { getShiftsWithSearch } from "./repository/ShiftReportRepository";

export interface ShiftOption {
	ShiftId: number;
	ProductionDate: Date;
	ProductionShift: number;
	Shift: string;
	Crew: string;
	Start: Date;
	End: Date;
}

interface ShiftSelectorProps {
	onShiftSelected: (shift: ShiftOption | null) => void;
	inputStyles?: React.CSSProperties;
	classes?: string;
	selectedShiftId?: number | null;
	disabled?: boolean;
}

const moment = getMomentTz();

export const ShiftSelectorAutocomplete = ({
	onShiftSelected,
	inputStyles,
	classes,
	selectedShiftId,
	disabled,
}: ShiftSelectorProps) => {
	const [shiftList, setShiftList] = useState<ShiftOption[]>([]);
	const [inputValue, setInputValue] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>("");
	const [value, setValue] = useState<ShiftOption | null>(null);
	const { shiftInfo } = useShiftNavigator();

	const fetchShifts = async () => {
		setLoading(true);
		const resp = await getShiftsWithSearch({ SearchTerm: "" });
		if (resp.ok) {
			setShiftList(resp.data);
		} else {
			setError(resp.message);
		}
		setLoading(false);
	};

	useEffect(() => {
		fetchShifts();
	}, []);

	useEffect(() => {
		if (shiftInfo?.CurrentShiftId && shiftList.length > 0) {
			const shift = shiftList.find((shift: ShiftOption) => {
				return (
					shift.ShiftId ===
					(selectedShiftId !== 0 ? selectedShiftId : shiftInfo?.CurrentShiftId)
				);
			});
			if (shift) {
				setValue(shift);
				onShiftSelected({
					Crew: shift.Crew,
					Shift: shift.Shift,
					ShiftId: shift.ShiftId,
					ProductionDate: shift.ProductionDate,
					ProductionShift: shift.ProductionShift,
					Start: shift.Start,
					End: shift.End,
				});
			}
		}
	}, [selectedShiftId, shiftList, shiftInfo, onShiftSelected]);

	return (
		<>
			<Grid container>
				<Grid item xs={12}>
					<Autocomplete
						options={shiftList}
						getOptionLabel={(option) => {
							return `${moment.utc(option.ProductionDate).format("MM/DD/YYYY")} - ${
								option.Shift
							} - ${option.Crew}`;
						}}
						loading={loading}
						value={value}
						onChange={(event, newValue) => {
							if (newValue) {
								setValue(newValue);
								onShiftSelected(newValue);
							}
						}}
						inputValue={inputValue}
						onInputChange={(event, newInputValue) =>
							setInputValue(newInputValue)
						}
						disabled={disabled}
						className={classes}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Shift"
								variant="outlined"
								InputLabelProps={{
									...params.InputLabelProps,
									shrink: true,
								}}
								InputProps={{
									...params.InputProps,
									style: inputStyles,
									endAdornment: (
										<>
											{loading ? (
												<CircularProgress color="inherit" size={20} />
											) : (
												params.InputProps.endAdornment
											)}
										</>
									),
								}}
							/>
						)}
					/>
				</Grid>
			</Grid>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
